interface IFilter {
  key: string;
  value: string;
  asValue?: string;
}

const tableQuery = (
  sort?: ITableEndpointProps['sort'],
  filters?: IFilter[],
  deleteId?: ITableEndpointProps['deleteId'],
  matchIds?: any,
) => {
  const sorting = sort ? `&sort=${sort.asc ? '' : '-'}${sort.accessor}` : '';
  const filtering = filters
    ? filters.map((filter) => `&filter[${filter.key}]=${encodeURIComponent(filter.value)}`).join('')
    : '';
  const deleting = deleteId ? `&deleteId=${deleteId}` : '';

  const matching = matchIds?.length ? matchIds.map((id: any) => `&filter[_id]=${id}`).join('') : '';

  return `${sorting}${filtering}${deleting}${matching}`;
};

const dateRangeQuery = (dateFrom: Date, dateTo: Date) => {
  let query = '';

  if (dateFrom) {
    query += `dateFrom=${dateFrom.toISOString()}`;
  }

  if (dateTo) {
    query += `&dateTo=${dateTo.toISOString()}`;
  }

  return query;
};

interface ITableEndpointProps {
  tableName: string;
  page?: number;
  sort?: {
    accessor: string;
    asc: boolean;
  };
  filter?: IFilter[];
  refresh?: boolean;
  deleteId?: string;
}

interface IReportEndpointProps {
  tableName: string;
  dateFrom: Date;
  dateTo: Date;
}

const tableEndpoint = ({ tableName, page, sort, filters, deleteId, matchIds, refresh }: any) =>
  `/v1/${tableName}/table?page=${page}${tableQuery(sort, filters, deleteId, matchIds)}${
    refresh ? '&refresh=true' : ''
  }`;

const tableFilterEndpoint = ({ tableName, page, sort, filter, deleteId, matchIds, refresh }: any) =>
  `/v1/${tableName}/filter?page=${page}${tableQuery(sort, filter, deleteId, matchIds)}`;

const exportTableEndpoint = ({ tableName, sort, filter }: ITableEndpointProps) =>
  `/v1/${tableName}/xls?${tableQuery(sort, filter)}`;

const exportReportEndpoint = ({ tableName, dateFrom, dateTo }: IReportEndpointProps) =>
  `/v1/${tableName}/xls?${dateRangeQuery(dateFrom, dateTo)}`;

export const endpoints = {
  resetCustomerPassword: '/v1/auth/reset-password',
  profile: '/v1/profile',
  login: '/v1/auth/login/admin',
  logOut: '/v1/auth/logOut/admin',
  carrierStatus: '/v1/system/carrier/api/health',
  table: tableEndpoint,
  tableFilter: tableFilterEndpoint,
  exportTable: exportTableEndpoint,
  exportReport: exportReportEndpoint,
  tableItem: (table: string, itemId?: string) => (itemId ? `v1/${table}/${itemId}` : `v1/${table}`).replace('//', '/'),
  constants: '/v1/constants',
  notifications: (templatesId: string[]) =>
    '/v1/notifications/email/templates?' + templatesId.map((template: string) => `templateType=${template}&`).join(''),
  purchaseReceipt: '/v1/notifications/purchaseReceipt/templates',
  links: '/v1/links',
  appVersions: '/v1/appVersions',
  supportReasons: '/v1/admin-panel/support-reasons',
  customers: '/v1/customers',
  partnerCodes: '/v1/partnerCodes',
  systemUsers: '/v1/systemUsers',
  promoCampaigns: '/v1/promoCampaigns',
  promoCampaignsPackages: '/v1/promoCampaigns/packages',
  appStats: (startDate: string, endDate: string) =>
    `/v1/admin/dashboard/app?filter[DateValueFrom]=${startDate}&filter[DateValueTo]=${endDate}`,
  activeUsers: (startDate: string, endDate: string) =>
    `/v1/admin/dashboard/users?filter[DateValueFrom]=${startDate}&filter[DateValueTo]=${endDate}`,
  imageUpload: '/v1/imageUpload',
  calculator: '/v1/calculators',
  tableAll: (table: string) => `/v1/${table}/table/all`,
  generateActivationCodes: '/v1/promoCodes/generate',
  promocodesGroups: (table: string) => `/v1/${table}/promocodesGroups`,
  disableAutoRenewPlan: `/v1/packages/disable-autorenewing`,
  a1Subscribers: `/v1/customers/subscribers`,
  removeEsims: `/v1/packages/remove-esims`,
  deleteTableItem: (tableName: string, _id: string) => `/v1/${tableName}/${_id}`,
  updatePackagesStatus: (idList: string[]) => '/v1/packages/status?' + idList.map((id) => 'id=' + id + '&').join(''),
  updatePartnerCodesStatus: (idList: string[], updateCampaign: boolean) =>
    '/v1/partnerCodes/status?' + idList.map((id) => 'id=' + id).join('&') + (updateCampaign ? `&updateCampaign=1` : ''),
  reservePromocodes: (idList: string[]) => '/v1/promoCodes/reserve?' + idList.map((id) => 'id=' + id + '&').join(''),
  unreservePromocodes: (idList: string[]) =>
    '/v1/promoCodes/unreserve?' + idList.map((id) => 'id=' + id + '&').join(''),
  resetAccounts: (customerId: string) => `/v1/customers/resetAccounts/${customerId}`,
  resetEsimsHistory: (customerId: string) => `/v1/customers/reset-esims-history/${customerId}`,
  resetCodes: (customerId: string) => `/v1/customers/reset-codes/${customerId}`,
  configs: () => `/v1/configs`,
  abusedPlans: `/v1/purchasedPackages/abused/`,
  paymentMethods: `/v1/configs/payment-methods`,
  partnerCodesRewardTypes: `/v1/partnerCodes/rewardTypes`,
  freePlans: `/v1/packages/free`,
  analyticsPackagesHistory: (range: any) => `/v1/packages/analytics-packages-history?from=${range.from}&to=${range.to}`,
  notificationsService: '/v1/notifications',
  sendNotification: (id: string) => `/v1/notifications/send/` + id,

  cruiseShips: '/v1/cruise-ships',
  cruiseShip: (id?: string) => (id ? `/v1/cruise-ships/${id}` : '/v1/cruise-ships'),
  cruiseLines: '/v1/cruise-lines',
  cruiseLine: (id?: string) => (id ? `/v1/cruise-lines/${id}` : '/v1/cruise-lines'),
};
